import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Bell } from "lucide-react";

export default function NotificationBell() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();

  const load = async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data.items || []);
      setUnread(data.unread || 0);
    } catch (e) { console.warn("notifications fetch failed", e); }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && unread > 0) {
      await api.post("/notifications/read-all");
      setUnread(0);
      setItems(prev => prev.map(x => ({ ...x, read: true })));
    }
  };

  const openItem = (n) => {
    setOpen(false);
    if (n.link) navigate(n.link);
  };

  return (
    <div ref={ref} className="relative">
      <button data-testid="notif-bell-btn" onClick={toggle}
        className="brut-btn-white px-3 py-2 text-sm relative" title="Notifications">
        <Bell strokeWidth={3} className="w-4 h-4"/>
        {unread > 0 && (
          <span data-testid="notif-unread-count"
            className="absolute -top-2 -right-2 bg-red-400 text-black text-[10px] font-fredoka font-bold border-2 border-black rounded-full w-5 h-5 flex items-center justify-center">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>
      {open && (
        <div data-testid="notif-panel" className="absolute right-0 mt-2 w-80 brut-card bg-white p-0 z-40 max-h-96 overflow-y-auto">
          <div className="px-4 py-3 border-b-2 border-black bg-lilac font-fredoka font-bold">Notifications</div>
          {items.length === 0 ? (
            <div className="p-4 text-sm text-gray-700">Nothing yet. Bid on an auction to get updates here.</div>
          ) : (
            <ul>
              {items.map(n => (
                <li key={n.notification_id}
                  onClick={() => openItem(n)}
                  className={`px-4 py-3 border-b border-black/20 cursor-pointer hover:bg-cream ${n.read ? "" : "bg-sunny/40"}`}
                  data-testid={`notif-item-${n.notification_id}`}>
                  <div className="font-bold text-sm">{n.title}</div>
                  <div className="text-xs text-gray-800 mt-0.5">{n.body}</div>
                  <div className="text-[10px] text-gray-500 mt-1">{new Date(n.created_at).toLocaleString()}</div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
